import * as Sentry from '@sentry/browser';
import { distanceUnitMap } from '~/constants/distance';
import { weightUnitMap } from '~/constants/weight';
import { PackageTemplateChooserFormValues } from '~/pages/common/PackageTemplateChooser/types';
import { RecipientType, ShipmentExtras } from '~/types/OrderBatchEntry';
import ParcelTemplate, {
  isParcelTemplate,
  JSONParcelTemplate,
  SavedJSONParcelTemplate,
  WebUserParcelTemplate,
  fromWebUserParcelTemplate,
} from '~/types/ParcelTemplate';
import {
  WebCarrierParcelTemplate,
  fromWebCarrierParcelTemplate,
} from '~/types/CarrierParcelTemplate';
import SignatureConfirmation from '~/types/SignatureConfirmation';
import CarrierParcelTemplate, {
  fromJSON as fromJSONCarrierParcelTemplate,
  JSONCarrierParcelTemplate,
  toJSON as toJSONCarrierParcelTemplate,
} from './CarrierParcelTemplate';
import {
  fromJSON as fromJSONParcelTemplate,
  toJSON as toJSONParcelTemplate,
} from './ParcelTemplate';

type Parcel = {
  length: string;
  width: string;
  height: string;
  distanceUnit: string;
  weight: string;
  weightUnit: string;
  userParcelTemplate: Maybe<ParcelTemplate>;
  carrierParcelTemplate: Maybe<CarrierParcelTemplate>;
  useImportedOrderWeight?: boolean;
};

export type WebParcel = {
  distance_unit: string;
  height: string;
  length: string;
  mass_unit: string;
  use_item_weight: boolean;
  weight: string;
  width: string;
  carrier_parcel_template?: WebCarrierParcelTemplate;
  user_parcel_template?: WebUserParcelTemplate;
};

export const unitValue = {
  length: '',
  width: '',
  height: '',
  distanceUnit: '',
  weight: '',
  useImportedAmount: false,
  userParcelTemplate: undefined,
  carrierParcelTemplate: undefined,
};

interface BaseJSONParcel {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight: string;
  mass_unit: string;
  carrier_parcel_template: Maybe<JSONCarrierParcelTemplate>;
  use_imported_order_weight?: boolean;
}

interface JSONParcel extends BaseJSONParcel {
  user_parcel_template: Maybe<JSONParcelTemplate>;
}

interface SavedJSONParcel extends BaseJSONParcel {
  user_parcel_template: Maybe<SavedJSONParcelTemplate>;
}

function fromJSON(jsonParcel: JSONParcel): Parcel {
  let weight = jsonParcel.weight;
  try {
    weight = Number(weight).toFixed(2);
  } catch (e) {
    Sentry.captureException(e);
  }
  return {
    weight,
    length: jsonParcel.length,
    width: jsonParcel.width,
    height: jsonParcel.height,
    distanceUnit: jsonParcel.distance_unit,
    weightUnit: jsonParcel.mass_unit,
    userParcelTemplate:
      jsonParcel.user_parcel_template && fromJSONParcelTemplate(jsonParcel.user_parcel_template),
    carrierParcelTemplate:
      jsonParcel.carrier_parcel_template &&
      fromJSONCarrierParcelTemplate(jsonParcel.carrier_parcel_template),
  };
}

function toJSON(parcel: Parcel): SavedJSONParcel {
  return {
    length: parcel.length,
    width: parcel.width,
    height: parcel.height,
    distance_unit: parcel.distanceUnit,
    weight: parcel.weight,
    mass_unit: parcel.weightUnit,
    user_parcel_template:
      parcel.userParcelTemplate && toJSONParcelTemplate(parcel.userParcelTemplate),
    carrier_parcel_template:
      parcel.carrierParcelTemplate && toJSONCarrierParcelTemplate(parcel.carrierParcelTemplate),
    use_imported_order_weight: parcel.useImportedOrderWeight,
  };
}

export const fromWebParcel = (webParcel: WebParcel): Parcel => {
  const userParcelTemplate = fromWebUserParcelTemplate(webParcel.user_parcel_template);
  const carrierParcelTemplate = fromWebCarrierParcelTemplate(webParcel.carrier_parcel_template);

  return {
    length: webParcel.length,
    width: webParcel.width,
    height: webParcel.height,
    distanceUnit: webParcel.distance_unit,
    weight: webParcel.weight,
    weightUnit: webParcel.mass_unit,
    useImportedOrderWeight: webParcel.use_item_weight,

    userParcelTemplate,
    carrierParcelTemplate,
  };
};

function getParcelData(data: PackageTemplateChooserFormValues): Parcel {
  // Logic copied from OrdersBatch
  if (
    data.extras.isUsingCustomDimensions &&
    data.customDimensions &&
    !data.extras.carrierTemplateWithCustomDimensions
  ) {
    return {
      length: data.customDimensions.length,
      width: data.customDimensions.width,
      height: data.customDimensions.height,
      distanceUnit: data.customDimensions.distanceUnit.value.toString(),
      weight: data.weight,
      weightUnit: data.weightUnit.value.toString(),
      userParcelTemplate: null,
      carrierParcelTemplate: null,
      useImportedOrderWeight: data.useImportedOrderWeight,
    };
  }

  const parcelTemplate = data.parcelTemplate;
  // if it's a user parcel template, get the dimensions from the template
  if (parcelTemplate && isParcelTemplate(parcelTemplate)) {
    // if it's derived from a carrier template, the dimensions are from the nested template
    if (parcelTemplate.carrierTemplate) {
      const carrierTemplate = parcelTemplate.carrierTemplate;

      return {
        length: carrierTemplate.length.toString(),
        width: carrierTemplate.width.toString(),
        height: carrierTemplate.height.toString(),
        distanceUnit: distanceUnitMap[carrierTemplate.distanceUnit],
        weight: data.weight,
        weightUnit: weightUnitMap[String(data.weightUnit.value)],
        userParcelTemplate:
          data.parcelTemplate && isParcelTemplate(data.parcelTemplate) ? data.parcelTemplate : null,
        carrierParcelTemplate: null,
        useImportedOrderWeight: data.useImportedOrderWeight,
      };
    }

    // otherwise the dimensions are from the parcel template
    return {
      length: parcelTemplate.length.toString(),
      width: parcelTemplate.width.toString(),
      height: parcelTemplate.height.toString(),
      distanceUnit: distanceUnitMap[parcelTemplate.distanceUnit],
      weight: data.weight,
      weightUnit: weightUnitMap[String(data.weightUnit.value)],
      userParcelTemplate:
        data.parcelTemplate && isParcelTemplate(data.parcelTemplate) ? data.parcelTemplate : null,
      carrierParcelTemplate: null,
      useImportedOrderWeight: data.useImportedOrderWeight,
    };
  }

  if (data.extras.carrierTemplateWithCustomDimensions && data.customDimensions) {
    return {
      length: data.customDimensions.length.toString(),
      width: data.customDimensions.width.toString(),
      height: data.customDimensions.height.toString() || '0',
      distanceUnit: data.customDimensions.distanceUnit.value.toString(),
      weight: data.weight,
      weightUnit: weightUnitMap[String(data.weightUnit.value)],
      userParcelTemplate: null,
      carrierParcelTemplate: parcelTemplate,
      useImportedOrderWeight: data.useImportedOrderWeight,
    };
  }

  // otherwise this is a carrier template
  return {
    length: parcelTemplate!.length.toString(),
    width: parcelTemplate!.width.toString(),
    height: parcelTemplate!.height.toString(),
    distanceUnit: distanceUnitMap[parcelTemplate!.distanceUnit],
    weight: data.weight,
    weightUnit: weightUnitMap[String(data.weightUnit.value)],
    userParcelTemplate: null,
    useImportedOrderWeight: data.useImportedOrderWeight,
    carrierParcelTemplate: parcelTemplate,
  };
}

export function getExtrasData(
  data: PackageTemplateChooserFormValues,
): Pick<
  ShipmentExtras,
  'alcohol' | 'dryIce' | 'isReturnRequested' | 'signatureConfirmation' | 'hazmat'
> {
  return {
    alcohol: data.alcohol.containsAlcohol
      ? {
          containsAlcohol: data.alcohol.containsAlcohol,
          recipientType: data.alcohol.recipientType.value as RecipientType,
        }
      : null,
    signatureConfirmation: data.alcohol.containsAlcohol
      ? {
          hasSignatureConfirmation: true,
          signatureConfirmationType: SignatureConfirmation.ADULT,
        }
      : undefined,
    dryIce: data.dryIce.containsDryIce
      ? {
          containsDryIce: data.dryIce.containsDryIce,
          weight: data.dryIce.weight,
          weightUnitId: data.dryIce.weightUnitId,
        }
      : null,
    hazmat: data.hazmat
      ? {
          isHazmat: data.hazmat.containsHazmat,
          isBiologicalMaterial: data.hazmat.containsBiologicalMaterial,
          isLithiumBatteries: data.hazmat.containsLithiumBatteries,
        }
      : undefined,
    isReturnRequested: data.isReturnRequested,
  };
}

function toParcelTemplate(parcel: Parcel): ParcelTemplate {
  const templateBase = {
    weight: parseFloat(parcel.weight),
    weightUnit: weightUnitMap[parcel.weightUnit],
  };

  if (parcel.userParcelTemplate) {
    return {
      ...parcel.userParcelTemplate,
      length: parseFloat(parcel.length),
      width: parseFloat(parcel.width),
      height: parseFloat(parcel.height),
      distanceUnit: distanceUnitMap[parcel.distanceUnit],
      ...templateBase,
    };
  }

  // Custom dimensions, or carrier template if parcel has it
  return {
    length: parseFloat(parcel.length),
    width: parseFloat(parcel.width),
    height: parseFloat(parcel.height),
    name: '',
    carrierTemplate: parcel.carrierParcelTemplate,
    isDefault: false,
    distanceUnit: distanceUnitMap[parcel.distanceUnit],
    ...templateBase,
  };
}

function fromParcelTemplate(parcelTemplate: ParcelTemplate): Parcel {
  return {
    length: parcelTemplate.length.toString(),
    width: parcelTemplate.width.toString(),
    height: parcelTemplate.height.toString(),
    distanceUnit: parcelTemplate.distanceUnit,
    weight: parcelTemplate.weight.toString(),
    weightUnit: parcelTemplate.weightUnit,
    userParcelTemplate: parcelTemplate,
    carrierParcelTemplate: null,
  };
}

// Get the name of the template used to create a parcel, with one exception: if a user parcel template was
// modified after it was used to create the parcel, and they no longer match, treat the parcel as if it had custom
// dimensions rather than a template
function getTemplateName(parcel: Parcel): Maybe<string> {
  return (
    parcel.userParcelTemplate?.carrierTemplate?.name ||
    (matchesTemplate(parcel, parcel.userParcelTemplate) && parcel.userParcelTemplate?.name) ||
    parcel.carrierParcelTemplate?.name ||
    null
  );
}

// Determines if a parcel's dimensions match a user template. Used to identify cases where a template was
// applied to create a parcel, but then the template was modified and no longer matches the parcel
function matchesTemplate(parcel: Parcel, template: ParcelTemplate | undefined | null): boolean {
  return (
    parseFloat(parcel.length) === template?.length &&
    parseFloat(parcel.width) === template?.width &&
    parseFloat(parcel.height) === template?.height &&
    parcel.distanceUnit === template?.distanceUnit?.toString()
  );
}

export default Parcel;
export {
  fromJSON,
  toJSON,
  JSONParcel,
  SavedJSONParcel,
  getParcelData,
  toParcelTemplate,
  fromParcelTemplate,
  getTemplateName,
  matchesTemplate,
  Parcel,
};
